import { Box, CircularProgress, DialogContent, InputAdornment } from "@mui/material"
import { LocationOnOutlined, PersonAddOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useState } from "react"

import type { SalesCustomer, SalesRequester } from "../../types"
import { ShipToLocationCard } from "../ShipToLocationCard"

import { ConfirmedHeaderSummary } from "./ConfirmedHeaderSummary"
import { NewRequestStepActions } from "./NewRequestStepActions"
import { StepSectionLabel } from "./StepSectionLabel"

import { AerosAutocomplete, AerosMultiSelect } from "@/components/UiKit"
import { FULL_LIST_LIMIT } from "@/constants/common"
import type { Location } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import colors from "@/styles/colors"

interface RequesterAndLocationsStepProps {
  customer: SalesCustomer
  onCancel: () => void
  onContinue: (requesters: SalesRequester[], locations: Location[]) => void
}

export const RequesterAndLocationsStep: FC<RequesterAndLocationsStepProps> = ({ customer, onCancel, onContinue }) => {
  const [selectedRequesterIds, setSelectedRequesterIds] = useState<string[]>([])
  const [removedLocationIds, setRemovedLocationIds] = useState<Set<string>>(new Set())
  const [locationFilter, setLocationFilter] = useState("")

  const { locations: allLocations, isLocationsFetching: isFetching } = useGetLocations(customer.company.companyId, {
    isLocationActive: true,
    limit: FULL_LIST_LIMIT,
  })

  const selectedLocations = useMemo(
    () => allLocations.filter((location) => !removedLocationIds.has(location.locationId)),
    [allLocations, removedLocationIds]
  )

  const unselectedLocations = useMemo(
    () => allLocations.filter((location) => removedLocationIds.has(location.locationId)),
    [allLocations, removedLocationIds]
  )

  const locationOptions = useMemo(
    () =>
      unselectedLocations.map((location) => ({
        value: location.locationId,
        label: [location.name, location.address1, location.city, location.regionIsoCode, location.postalCode]
          .filter(Boolean)
          .join(", "),
      })),
    [unselectedLocations]
  )

  const availableRequesters = customer.productManagers
  const requesterOptions = useMemo(
    () =>
      availableRequesters.map((requester) => ({
        value: requester.userId,
        label: `${requester.firstName} ${requester.lastName}`,
      })),
    [availableRequesters]
  )

  const handleLocationRemove = (locationId: string) => {
    setRemovedLocationIds((prev) => {
      const next = new Set(prev)
      next.add(locationId)
      return next
    })
  }

  const handleLocationAdd = (locationId: string) => {
    setRemovedLocationIds((prev) => {
      if (!prev.has(locationId)) return prev
      const next = new Set(prev)
      next.delete(locationId)
      return next
    })
    setLocationFilter("")
  }

  const canContinue = selectedRequesterIds.length > 0

  const handleContinue = () => {
    const selectedRequesters = availableRequesters.filter((requester) =>
      selectedRequesterIds.includes(requester.userId)
    )
    onContinue(selectedRequesters, selectedLocations)
  }

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-8 py-6'>
        <ConfirmedHeaderSummary formValues={{ customer }} />

        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<PersonAddOutlined />} label='Select Requester' required />
          <Box sx={{ "& .MuiChip-root": { backgroundColor: colors.grey[200] } }}>
            <AerosMultiSelect
              withSelectAll
              options={requesterOptions}
              value={selectedRequesterIds}
              onChange={setSelectedRequesterIds}
              placeholder='Select requesters...'
              disabled={availableRequesters.length === 0}
              data-testid='sales.requester-and-locations-step.requester-field'
            />
          </Box>
        </Box>

        <Box className='flex flex-col gap-2'>
          <StepSectionLabel icon={<LocationOnOutlined />} label='Customer Location(s)' />

          {isFetching && allLocations.length === 0 && (
            <Box className='flex justify-center py-2'>
              <CircularProgress size={24} />
            </Box>
          )}

          {selectedLocations.length > 0 && (
            <Box className='flex flex-col gap-2'>
              {selectedLocations.map((location) => (
                <ShipToLocationCard key={location.locationId} location={location} onRemove={handleLocationRemove} />
              ))}
            </Box>
          )}

          {unselectedLocations.length > 0 && (
            <AerosAutocomplete
              options={locationOptions}
              value={null}
              inputValue={locationFilter}
              onInputChange={setLocationFilter}
              onChange={(locationId) => {
                if (locationId) handleLocationAdd(locationId)
              }}
              forcePopupIcon={false}
              placeholder='Search locations...'
              slotProps={{
                textField: {
                  slotProps: {
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchOutlined className='text-gray-600' />
                        </InputAdornment>
                      ),
                    },
                  },
                },
              }}
              data-testid='sales.requester-and-locations-step.location-filter'
            />
          )}
        </Box>
      </DialogContent>

      <NewRequestStepActions
        onCancel={onCancel}
        onContinue={handleContinue}
        continueDisabled={!canContinue}
        cancelTestId='sales.requester-and-locations-step.cancel-button'
        continueTestId='sales.requester-and-locations-step.continue-button'
      />
    </>
  )
}
